import { computed } from 'vue';
import type { LiabilityFormLayout } from '../types';
import { EAssetMaintainer, getRefId, type FullInvestment } from 'findef';
import { DetailCard } from 'components/detail-card';
import { prettyNumber } from '@/utils/number/format';
import { prettyAssetSubtypes, prettyAssetType } from '@/utils/asset';
import { defineComponent } from 'vue';
import { useUserSettings, type UseUserSettings } from '@/modules/settings/hooks/useUserSettings';
import {
  useForexTickers,
  type UseForexTickers,
} from '@/integrations/findex/hooks/forex/useForexTickers';
import { getInvestmentCurrentValue, getInvestmentCurrentValueCurrency } from '@/utils/investment';
import { AssetImageV2 } from 'components/asset-image-v2';
import { getMaintainedText } from '@/utils/asset/maintained';
import type { FindexAsset } from '@/types/models/asset';

const InvestmentCard = defineComponent<{
  investment: FullInvestment;
  settings: UseUserSettings;
  forex: UseForexTickers;
  onClose?: () => any;
  removeIsDisabled?: boolean;
}>(
  (props) => {
    const inv = computed(() => props.investment);

    const currentValueCurrency = computed(() => getInvestmentCurrentValueCurrency(inv.value));

    const currentValueTicker = computed(
      () =>
        props.forex.getTickerRef(
          currentValueCurrency.value || props.settings.currency.value,
          props.settings.currency.value,
        ).value,
    );

    const currentValue = computed(() => getInvestmentCurrentValue(inv.value));
    const currentValueConverted = computed(() => currentValue.value * currentValueTicker.value.bid);

    const assetType = computed(() =>
      prettyAssetType(inv.value.asset?.type, inv.value.asset?.listed, undefined, false, true),
    );

    const assetSubtype = computed(() => prettyAssetSubtypes(inv.value.asset.subtypes || [], true));
    const prettyType = computed(() => {
      return [assetType.value, inv.value?.asset?.subtypes ? `(${assetSubtype.value})` : null]
        .filter((it) => typeof it === 'string')
        .filter((it) => it.length > 1)
        .join(' ');
    });

    const asset = props.investment.asset as FindexAsset;
    const providerImport = props.investment?.providerImport;
    const provider =
      providerImport && typeof providerImport !== 'string' && 'provider' in (providerImport as any)
        ? (providerImport as any).provider
        : undefined;

    const maintained = getMaintainedText(asset, provider);
    const maintainedText =
      maintained.toLowerCase() === EAssetMaintainer.PROVIDER.toLowerCase()
        ? provider?.displayName || EAssetMaintainer.PROVIDER.toLowerCase()
        : maintained;

    const handleRemove = () => {
      if (props.removeIsDisabled) return;
      if (props.onClose) {
        props.onClose();
      }
    };

    const iconContent = computed(() => {
      return (
        <AssetImageV2
          size="md"
          variant="gray"
          objectFit="cover"
          investment={inv.value}
          asset={inv.value.asset}
          iconProps={{ size: "1.25rem" }}
        />
      );
    });

    return () => {
      return (
        <DetailCard
          key={getRefId(inv.value)}
          title={inv.value.asset?.name || '?'}
          subtitle={`${prettyType.value}`}
          badgeText={prettyNumber(currentValueConverted.value, {
            american: true,
            maxDecimals: 2,
            zeroIsInteger: true,
            suffix: props.settings.currency.value,
          })}
          badgeMaintained={{
            name: provider?.name,
            iconUrl: provider?.iconUrl,
            title: maintainedText,
          }}
          onClose={handleRemove}
          iconContent={iconContent}
          icon={{
            src: async () => import('assets/img/svg/icons/receipt.svg?raw'),
            size: '1.25rem',
            stroke: 'none',
            fill: 'currentColor',
          }}
          closeIsDisabled={props.removeIsDisabled}
        />
      );
    };
  },
  { props: ['investment', 'onClose', 'settings', 'forex', 'removeIsDisabled'] },
);

export const investmentsLayout = (): LiabilityFormLayout => {
  return {
    children: (state, form) => {
      const children: LiabilityFormLayout[] = [{ render: 'hasInvestments', key: 'hasInvestments' }];

      //const children: UseAssetFormLayout[] = [
      //  { render: 'hasInvestmentsAttached', key: 'hasInvestmentsAttached' },
      //  ...(state.hasInvestmentsAttached
      //    ? [{ render: 'selectInvility', key: 'selectInvility' }]
      //    : [])
      //]

      if (state.hasInvestments) {
        children.push({
          render: 'investments',
          key: 'investments',
        });
        if (state.investments && Array.isArray(state.investments) && state.investments.length > 0) {
          children.push({
            key: 'invesment-list',
            component: (_state, form) => {
              const settings = useUserSettings();
              const forex = useForexTickers();

              return (props) => {
                const state = props.state.value;
                console.log(form.props.externalData);

                const investments = computed(() => {
                  const data = state.investments;
                  return Array.isArray(data) ? (data as FullInvestment[]) : [];
                });
                const handleRemove = (inv: FullInvestment) => {
                  if (form.props.externalData?.currentInvestment?.value) return;
                  form.updateFieldValue('investments', (investments) => {
                    if (!Array.isArray(investments)) return [];
                    return investments.filter((it) => getRefId(it) !== getRefId(inv));
                  });
                };

                return (
                  <div
                    class="grid grid-cols-1 gap-4"
                  >
                    {investments.value
                      .filter((it) => !!it.asset)
                      .map((inv) => {
                        return (
                          <InvestmentCard
                            key={getRefId(inv)}
                            investment={inv}
                            onClose={() => handleRemove(inv)}
                            settings={settings}
                            forex={forex}
                          />
                        );
                      })}
                  </div>
                );
              };
            },
          });
        }
        if (form.props.externalData?.currentInvestment?.value) {
          children.push({
            key: 'current-investment',
            component: (_state, form) => {
              const settings = useUserSettings();
              const forex = useForexTickers();

              return () => {
                return (
                  <div
                    class="grid grid-auto-rows gap-4"
                    style={{
                      gridTemplateColumns: '1fr',
                    }}
                  >
                    {form.props.externalData?.currentInvestment?.value && (
                      <InvestmentCard
                        key={'MOCK'}
                        investment={form.props.externalData?.currentInvestment?.value}
                        settings={settings}
                        forex={forex}
                        removeIsDisabled={true}
                      />
                    )}
                  </div>
                );
              };
            },
          });
        }
      }

      return children;
    },
  };
};
